/**
 * AgoraService — wrapper around the Agora Web RTC SDK.
 *
 * Loads the Agora SDK from CDN on demand (browser-only).
 * The SDK is loaded via a <script> tag since it's not bundled to avoid
 * adding a large dependency (~2MB) to the main bundle.
 *
 * Usage:
 *   const service = new AgoraService();
 *   await service.load();
 *   const client = service.createClient();
 */

const AGORA_SDK_URL = 'https://download.agora.io/sdk/release/AgoraRTC_N-4.21.0.js';

class AgoraService {
  constructor() {
    this._sdk = null;
    this._client = null;
    this._localTracks = {};
  }

  /**
   * Load Agora SDK from CDN.
   * No-op if already loaded.
   */
  async load() {
    if (this._sdk) return this._sdk;

    if (typeof window === 'undefined') {
      throw new Error('AgoraService can only be used in the browser');
    }

    if (window.AgoraRTC) {
      this._sdk = window.AgoraRTC;
      return this._sdk;
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = AGORA_SDK_URL;
      script.async = true;
      script.onload = () => {
        this._sdk = window.AgoraRTC;
        resolve(this._sdk);
      };
      script.onerror = () => reject(new Error('Failed to load Agora SDK'));
      document.head.appendChild(script);
    });
  }

  getSdk() {
    return this._sdk || (typeof window !== 'undefined' ? window.AgoraRTC : null);
  }

  /**
   * Create and return the Agora RTC client.
   */
  createClient() {
    const sdk = this.getSdk();
    if (!sdk) throw new Error('Agora SDK not loaded');
    if (!this._client) {
      this._client = sdk.createClient({ mode: 'rtc', codec: 'h264' });
    }
    return this._client;
  }

  /**
   * Join a channel and publish local tracks.
   *
   * @param {string} appId
   * @param {string} channel
   * @param {string} token
   * @param {number} uid
   */
  async joinChannel(appId, channel, token, uid) {
    const client = this.createClient();
    const sdk = this.getSdk();
    await client.join(appId, channel, token, uid);

    // Create local audio and video tracks
    const [audioTrack, videoTrack] = await sdk.createMicrophoneAndCameraTracks();
    this._localTracks = { audioTrack, videoTrack };
    await client.publish([audioTrack, videoTrack]);
    return { audioTrack, videoTrack };
  }

  /**
   * Leave the channel and stop local tracks.
   */
  async leaveChannel() {
    const { audioTrack, videoTrack } = this._localTracks;
    if (audioTrack) { audioTrack.stop(); audioTrack.close(); }
    if (videoTrack) { videoTrack.stop(); videoTrack.close(); }
    this._localTracks = {};
    if (this._client) {
      await this._client.leave();
    }
  }

  /**
   * Toggle local audio mute.
   */
  async setMuted(muted) {
    const { audioTrack } = this._localTracks;
    if (audioTrack) {
      await audioTrack.setEnabled(!muted);
    }
  }

  /**
   * Toggle local video.
   */
  async setVideoEnabled(enabled) {
    const { videoTrack } = this._localTracks;
    if (videoTrack) {
      await videoTrack.setEnabled(enabled);
    }
  }
}

export default AgoraService;
